/**
 * DocumentsLink — Reusable component to attach/view documents linked to any entity.
 *
 * Usage:
 *   <DocumentsLink module="bcm" entityId="BCM-1234" title="Plano de Continuidade" />
 *
 * Behaviour:
 * - Shows a compact button with document count (e.g. "3 docs").
 * - Click opens a modal with linked documents + quick actions:
 *   - View/download existing docs
 *   - Attach new doc (opens create dialog pre-filled with module + entity_id)
 *   - Approval status badges
 */
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { FileText, Paperclip, Upload, ExternalLink, Download, Send, CheckCircle2, Loader2, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CATEGORY_LABELS = {
  policy: 'Política', procedure: 'Procedimento', plan: 'Plano', evidence: 'Evidência',
  certificate: 'Certificado', contract: 'Contrato', audit_report: 'Relatório Auditoria',
  incident_report: 'Relatório Incidente', risk_assessment: 'Avaliação de Risco',
  dpia_document: 'DPIA', training_material: 'Formação', other: 'Outro',
};

const STATUS_META = {
  draft: { label: 'Rascunho', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  pending_approval: { label: 'Pendente', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Aprovado', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejeitado', tone: 'bg-red-50 text-red-700 border-red-200' },
  archived: { label: 'Arquivado', tone: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const DEFAULT_CATEGORY_BY_MODULE = {
  bcm: 'plan',
  audit: 'audit_report',
  dpo: 'dpia_document',
  requirement: 'evidence',
  incident: 'incident_report',
  risk: 'risk_assessment',
  supplier: 'contract',
  training: 'training_material',
  policy: 'policy',
  asset: 'evidence',
  board: 'evidence',
  general: 'evidence',
};

export function DocumentsLink({ module, entityId, title, variant = 'compact', onChange }) {
  const { user } = useAuth();
  const canCreate = user?.role === 'admin' || user?.role === 'ciso' || user?.role === 'dpo';
  const isBoardMember = user?.role === 'board_member' || user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [attaching, setAttaching] = useState(false);
  const [form, setForm] = useState({
    title: title ? `Evidência: ${title}` : '',
    description: '',
    category: DEFAULT_CATEGORY_BY_MODULE[module] || 'evidence',
    requires_board_approval: false,
    external_url: '',
  });
  const [fileB64, setFileB64] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileMime, setFileMime] = useState('');
  const fileInputRef = useRef(null);

  // Lightweight count fetch for compact button
  const [count, setCount] = useState(0);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!module || !entityId) return;
      try {
        const { data } = await api.get(`/documents?linked_module=${module}&linked_entity_id=${entityId}&page_size=1`);
        if (!ignore) setCount(data.total || 0);
      } catch { /* silent */ }
    })();
    return () => { ignore = true; };
  }, [module, entityId, refresh]);

  useEffect(() => {
    let ignore = false;
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/documents?linked_module=${module}&linked_entity_id=${entityId}&page_size=50`);
        if (!ignore) setItems(data.items || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar.');
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [open, module, entityId, refresh]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Ficheiro excede 10 MB. Utilize URL externo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(',')[1] || result;
      setFileB64(base64);
      setFileName(f.name);
      setFileMime(f.type || 'application/octet-stream');
    };
    reader.readAsDataURL(f);
  };

  const attach = async () => {
    if (!form.title || !form.category) { toast.error('Título e categoria obrigatórios.'); return; }
    if (!fileB64 && !form.external_url) { toast.error('Anexe um ficheiro ou URL externo.'); return; }
    setAttaching(true);
    try {
      await api.post('/documents', {
        ...form,
        linked_module: module,
        linked_entity_id: entityId,
        content_base64: fileB64 || undefined,
        file_name: fileName || undefined,
        mime_type: fileMime || undefined,
        external_url: form.external_url || undefined,
      });
      toast.success('Documento anexado.');
      setForm({ ...form, title: title ? `Evidência: ${title}` : '', description: '', external_url: '' });
      setFileB64(null); setFileName(''); setFileMime('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setRefresh(r => r + 1);
      onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
    finally { setAttaching(false); }
  };

  const submitForApproval = async (docId) => {
    try {
      await api.post(`/documents/${docId}/submit`);
      toast.success('Submetido ao Órgão de Gestão.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const approve = async (docId) => {
    if (!window.confirm('Confirma aprovação? Esta ação fica assinada digitalmente (SHA-256).')) return;
    try {
      await api.post(`/documents/${docId}/approve`);
      toast.success('Aprovado e assinado.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.doc_id}/download`);
      if (data.external_url) {
        window.open(data.external_url, '_blank', 'noopener');
        return;
      }
      if (data.content_base64) {
        const link = document.createElement('a');
        link.href = `data:${data.mime_type || 'application/octet-stream'};base64,${data.content_base64}`;
        link.download = data.file_name || `${doc.doc_id}.bin`;
        link.click();
      } else {
        toast.info('Documento sem ficheiro anexado.');
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`docs-link-${module}-${entityId}`}
        className="text-xs h-7 gap-1.5"
        title="Documentos anexados"
      >
        <Paperclip className="h-3.5 w-3.5" />
        {variant === 'compact' ? (
          count > 0 ? <span className="font-semibold">{count}</span> : 'Anexar'
        ) : (
          <>Documentos {count > 0 && <Badge variant="secondary" className="ml-1 bg-slate-100 text-slate-700 border-slate-200">{count}</Badge>}</>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Documentos {title && `· ${title}`}
            </DialogTitle>
            <DialogDescription>
              Vinculados a <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{module}/{entityId}</code>. Documentos aprovados são evidência formal para auditorias.
            </DialogDescription>
          </DialogHeader>

          {/* Existing list */}
          <div className="space-y-2">
            {loading ? (
              <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-indigo-600" /></div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400 bg-slate-50 rounded-lg border border-slate-200 border-dashed">
                Sem documentos vinculados. Anexe abaixo o primeiro.
              </div>
            ) : (
              items.map(d => {
                const sm = STATUS_META[d.status] || {};
                const canApprove = isBoardMember && d.status === 'pending_approval';
                const canSubmit = canCreate && (d.status === 'draft' || d.status === 'rejected') && d.requires_board_approval;
                return (
                  <div key={d.doc_id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50" data-testid={`docs-link-item-${d.doc_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 text-sm">{d.title}</span>
                          <Badge variant="secondary" className={`text-[10px] ${sm.tone}`}>{sm.label}</Badge>
                          <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">{CATEGORY_LABELS[d.category]}</Badge>
                        </div>
                        {d.approved_at && (
                          <div className="text-[11px] text-emerald-600 flex items-center gap-1 mt-1">
                            <CheckCircle2 className="h-3 w-3" /> Aprovado por {d.approved_by_name || d.approved_by} em {d.approved_at.slice(0, 10)}
                          </div>
                        )}
                        {d.sha256_hash && <code className="text-[10px] text-slate-500 font-mono">SHA-256: {d.sha256_hash.slice(0, 16)}…</code>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => download(d)}>
                          {d.external_url ? <ExternalLink className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
                        </Button>
                        {canSubmit && <Button variant="outline" size="sm" onClick={() => submitForApproval(d.doc_id)}><Send className="h-3.5 w-3.5 mr-1" />Submeter</Button>}
                        {canApprove && <Button variant="outline" size="sm" onClick={() => approve(d.doc_id)} className="text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Aprovar</Button>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Attach new */}
          {canCreate && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2">
                <Plus className="h-3.5 w-3.5" /> Anexar novo documento
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Título *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="docs-link-form-title" /></div>
                <div>
                  <Label className="text-xs">Categoria</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="docs-link-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea rows={2} placeholder="Descrição (opcional)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" onChange={handleFile} className="hidden" id={`docs-link-file-${entityId}`} data-testid="docs-link-file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1.5" /> Ficheiro (≤10 MB)
                </Button>
                {fileName && <span className="text-xs text-slate-500 truncate">{fileName}</span>}
                <span className="text-xs text-slate-400">ou</span>
                <Input placeholder="URL externo (Drive, SharePoint…)" value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} className="text-xs" />
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.requires_board_approval} onChange={e => setForm({ ...form, requires_board_approval: e.target.checked })} data-testid="docs-link-form-approval" />
                <span>Requer aprovação do Órgão de Gestão (NIS2 Art. 20)</span>
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Fechar</Button>
            {canCreate && (
              <Button onClick={attach} disabled={attaching} data-testid="docs-link-attach">
                {attaching ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A anexar…</> : <><Plus className="h-4 w-4 mr-1.5" />Anexar</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
